import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Loader2, ArrowLeft, ArrowRight, CheckCircle2, Sparkles, PhoneCall, Wand2 } from "lucide-react";
import { toast } from "sonner";

const INTERVENTIONS = [
  { id: "ristrutturazione_interni", title: "Ristrutturazione interni", desc: "Appartamento, ufficio, negozio" },
  { id: "ristrutturazione_fabbricato", title: "Ristrutturazione fabbricato", desc: "Palazzo o edificio intero" },
  { id: "demolizione_ricostruzione", title: "Demolizione e ricostruzione", desc: "Abbattere e ricostruire" },
  { id: "edificazione_ex_novo", title: "Edificazione ex novo", desc: "Costruire su terreno" },
];

const PROPERTY_TYPES = [
  { id: "appartamento", title: "Appartamento" },
  { id: "villa_villetta", title: "Villa / villetta" },
  { id: "ufficio", title: "Ufficio" },
  { id: "locale_commerciale", title: "Locale commerciale" },
  { id: "fabbricato_palazzo", title: "Fabbricato / palazzo intero" },
  { id: "terreno", title: "Terreno edificabile" },
];

const STATES = [
  { id: "buono", title: "Buono", desc: "Serve solo un aggiornamento" },
  { id: "da_rinfrescare", title: "Da rinfrescare", desc: "Piccoli interventi diffusi" },
  { id: "da_rifare", title: "Da rifare completamente", desc: "Ristrutturazione integrale" },
];

const TIMELINES = [
  { id: "urgente", title: "Urgente", desc: "Entro 3 mesi" },
  { id: "entro_6_mesi", title: "Entro 6 mesi", desc: "Ho una scadenza morbida" },
  { id: "entro_12_mesi", title: "Entro 12 mesi", desc: "Sto pianificando" },
  { id: "da_valutare", title: "Da valutare", desc: "Non ho fretta" },
];

const STEPS_LABELS = ["Progetto", "Contatti", "Analisi"];

export default function SimulatoreWizard() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    intervention_type: "",
    property_type: "",
    sqm: 90,
    current_state: "",
    city: "",
    timeline: "",
    notes: "",
    // Honeypot antibot: campo nascosto, gli utenti reali non lo compilano.
    // Se un bot lo compila, il backend scarta la richiesta senza chiamare Claude.
    website: "",
  });
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    privacy_consent: false,
    marketing_consent: false,
  });
  const [analysis, setAnalysis] = useState(null); // { analysis, disclaimer }
  const [loading, setLoading] = useState(false);
  const [submittedLead, setSubmittedLead] = useState(false);

  const totalSteps = STEPS_LABELS.length; // 3
  const progress = useMemo(() => ((step + 1) / totalSteps) * 100, [step]);

  useEffect(() => {
    track("analizzatore_iniziato");
  }, []);

  const step1Valid =
    !!data.intervention_type &&
    !!data.property_type &&
    data.sqm > 0 &&
    !!data.current_state &&
    !!data.city.trim() &&
    !!data.timeline;

  const step2Valid =
    !!contact.name.trim() &&
    /\S+@\S+\.\S+/.test(contact.email) &&
    contact.privacy_consent;

  const runAnalysisAndSaveLead = async (e) => {
    if (e) e.preventDefault();
    if (!step2Valid) {
      toast.error("Compila nome, email valida e consenso privacy.");
      return;
    }
    setLoading(true);
    setStep(2);
    // Timeout esplicito: se Claude o la rete tardano oltre 90s, mostriamo errore
    // invece di lasciare il wizard in "Analisi in corso" all'infinito.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    try {
      // 1) Chiamata Claude per l'analisi
      const res = await api.post("/analizzatore", data, { signal: controller.signal });
      const out = res.data; // { analysis, disclaimer }
      setAnalysis(out);

      // 2) Salvataggio lead + email dispatch (background)
      await api.post("/simulator/lead", {
        input: data,
        analysis: out.analysis,
        name: contact.name,
        email: contact.email,
        phone: contact.phone || null,
        privacy_consent: contact.privacy_consent,
        marketing_consent: contact.marketing_consent,
      });
      setSubmittedLead(true);
      track("analizzatore_completato", {
        tipo_intervento: data.intervention_type,
        mq: data.sqm,
      });
      toast.success("Analisi pronta. Ti ricontattiamo entro 48 ore.");
    } catch (err) {
      const msg = err?.name === "CanceledError" || err?.code === "ERR_CANCELED"
        ? "L'analisi ha impiegato troppo tempo. Riprova o contattaci direttamente."
        : err?.response?.data?.detail ||
          "Non è stato possibile generare l'analisi. Riprova o contattaci direttamente.";
      toast.error(msg);
      // Torna allo step contatti così l'utente può riprovare
      setStep(1);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  return (
    <div className="risco-card p-6 md:p-10" data-testid="simulatore-wizard">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs text-risco-muted mb-2">
          <div>Step {Math.min(step + 1, totalSteps)} / {totalSteps}</div>
          <div>{STEPS_LABELS[Math.min(step, totalSteps - 1)]}</div>
        </div>
        <div className="h-1.5 bg-risco-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-risco-accent transition-all duration-500"
            style={{ width: `${progress}%` }}
            data-testid="wizard-progress"
          />
        </div>
      </div>

      {/* Step 0: progetto */}
      {step === 0 && (
        <div data-testid="sim-step-progetto">
          <h3 className="font-heading text-2xl text-risco-ink">Parliamo del tuo progetto</h3>
          <p className="text-risco-muted mt-2">
            Poche informazioni essenziali: la nostra AI produrrà un&apos;analisi qualitativa del tuo intervento.
          </p>

          {/* Tipo intervento */}
          <div className="mt-8">
            <div className="overline mb-3">Tipo di intervento *</div>
            <div className="grid md:grid-cols-2 gap-3">
              {INTERVENTIONS.map((i) => (
                <button
                  key={i.id}
                  type="button"
                  onClick={() => setData({ ...data, intervention_type: i.id })}
                  data-testid={`sim-intervention-${i.id}`}
                  className={`p-4 rounded-lg border text-left transition-colors ${
                    data.intervention_type === i.id
                      ? "border-risco-accent bg-risco-accent-soft"
                      : "border-stone-300 hover:border-stone-500 bg-white"
                  }`}
                >
                  <div className="font-heading text-risco-ink">{i.title}</div>
                  <div className="text-sm text-risco-muted mt-1">{i.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Tipologia immobile */}
          <div className="mt-8">
            <div className="overline mb-3">Tipologia immobile *</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {PROPERTY_TYPES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setData({ ...data, property_type: p.id })}
                  data-testid={`sim-property-${p.id}`}
                  className={`p-3 rounded-lg border text-center transition-colors ${
                    data.property_type === p.id
                      ? "border-risco-accent bg-risco-accent-soft"
                      : "border-stone-300 hover:border-stone-500 bg-white"
                  }`}
                >
                  <div className="text-risco-ink text-sm">{p.title}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Superficie + città */}
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div>
              <label className="text-sm text-stone-700">Superficie indicativa (mq) *</label>
              <input
                type="number"
                min={1}
                value={data.sqm}
                onChange={(e) => setData({ ...data, sqm: parseInt(e.target.value) || 0 })}
                data-testid="sim-input-sqm"
                className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none"
              />
            </div>
            <div>
              <label className="text-sm text-stone-700">Città o zona *</label>
              <input
                value={data.city}
                onChange={(e) => setData({ ...data, city: e.target.value })}
                data-testid="sim-input-city"
                placeholder="es. Napoli, Vomero"
                className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none"
              />
            </div>
          </div>

          {/* Stato attuale */}
          <div className="mt-8">
            <div className="overline mb-3">Stato attuale dell&apos;immobile *</div>
            <div className="grid md:grid-cols-3 gap-3">
              {STATES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setData({ ...data, current_state: s.id })}
                  data-testid={`sim-state-${s.id}`}
                  className={`p-4 rounded-lg border text-left transition-colors ${
                    data.current_state === s.id
                      ? "border-risco-accent bg-risco-accent-soft"
                      : "border-stone-300 hover:border-stone-500 bg-white"
                  }`}
                >
                  <div className="font-heading text-risco-ink">{s.title}</div>
                  <div className="text-sm text-risco-muted mt-1">{s.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="mt-8">
            <div className="overline mb-3">Quando vorresti iniziare? *</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {TIMELINES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setData({ ...data, timeline: t.id })}
                  data-testid={`sim-timeline-${t.id}`}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    data.timeline === t.id
                      ? "border-risco-accent bg-risco-accent-soft"
                      : "border-stone-300 hover:border-stone-500 bg-white"
                  }`}
                >
                  <div className="font-heading text-risco-ink text-sm">{t.title}</div>
                  <div className="text-xs text-risco-muted mt-1">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Note libere */}
          <div className="mt-8">
            <label className="text-sm text-stone-700">Note aggiuntive (facoltativo)</label>
            <textarea
              rows={3}
              value={data.notes}
              onChange={(e) => setData({ ...data, notes: e.target.value })}
              data-testid="sim-input-notes"
              placeholder="Es. voglio abbattere un muro, mi interessa la domotica, immobile con vincoli paesaggistici…"
              className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none"
            />
          </div>

          {/* Honeypot antibot: visivamente nascosto ma presente nel DOM.
              Gli utenti reali non lo vedono; i bot che compilano tutti i campi lo attivano. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
            <label htmlFor="sim-hp-website">Sito web (non compilare)</label>
            <input
              type="text"
              id="sim-hp-website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={data.website}
              onChange={(e) => setData({ ...data, website: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* Step 1: contatti + consensi */}
      {step === 1 && (
        <div data-testid="sim-step-contatti">
          <h3 className="font-heading text-2xl text-risco-ink">Dove ti mandiamo l&apos;analisi?</h3>
          <p className="text-risco-muted mt-2">
            Riceverai il documento via email. Il nostro team ti ricontatterà entro 48 ore per proporti un sopralluogo gratuito.
          </p>

          <form className="mt-8 space-y-4" onSubmit={runAnalysisAndSaveLead}>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-stone-700">Nome e cognome *</label>
                <input
                  required
                  value={contact.name}
                  onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  data-testid="sim-lead-name"
                  className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none"
                />
              </div>
              <div>
                <label className="text-sm text-stone-700">Email *</label>
                <input
                  required
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  data-testid="sim-lead-email"
                  className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-stone-700">Telefono (facoltativo)</label>
              <input
                type="tel"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                data-testid="sim-lead-phone"
                className="mt-1 w-full bg-white border border-stone-300 rounded-md px-3 py-3 text-risco-ink focus:border-risco-accent outline-none"
              />
            </div>

            <div className="pt-2 space-y-3">
              <label className="flex items-start gap-3 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={contact.privacy_consent}
                  onChange={(e) => setContact({ ...contact, privacy_consent: e.target.checked })}
                  data-testid="sim-lead-privacy"
                  className="mt-1 accent-risco-accent"
                />
                <span>
                  * Ho letto la{" "}
                  <Link to="/privacy" className="text-risco-accent underline underline-offset-2">
                    Privacy Policy
                  </Link>{" "}
                  e acconsento al trattamento dei dati per essere ricontattato in merito al progetto.
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-stone-700">
                <input
                  type="checkbox"
                  checked={contact.marketing_consent}
                  onChange={(e) => setContact({ ...contact, marketing_consent: e.target.checked })}
                  data-testid="sim-lead-marketing"
                  className="mt-1 accent-risco-accent"
                />
                <span>
                  Acconsento a ricevere occasionali comunicazioni Ri.Sco. su progetti,
                  bonus fiscali e novità del settore (facoltativo).
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={!step2Valid || loading}
              data-testid="sim-generate-btn"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 py-4 rounded-md bg-risco-accent hover:bg-risco-accent-dark disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Analisi in corso…
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4" /> Genera analisi con l&apos;AI
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Step 2: risultato AI */}
      {step === 2 && (
        <div data-testid="sim-step-analisi">
          {loading && !analysis && (
            <div className="text-center py-16" data-testid="sim-loading">
              <Loader2 className="w-12 h-12 text-risco-accent mx-auto animate-spin" />
              <div className="mt-6 overline">Analisi in corso</div>
              <h3 className="font-heading text-2xl text-risco-ink mt-2">
                La nostra AI sta preparando il tuo report
              </h3>
              <p className="text-risco-muted mt-3 max-w-md mx-auto">
                Fasi di lavoro, permessi, voci di costo e tempi. Ci vogliono circa 20-30 secondi.
              </p>
            </div>
          )}

          {analysis && (
            <div>
              <div className="overline mb-2 inline-flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> Analisi del progetto
              </div>
              <h3
                className="font-heading text-3xl md:text-4xl text-risco-ink tracking-tight leading-tight"
                data-testid="sim-analysis-title"
              >
                Il tuo progetto,{" "}
                <span className="font-display italic text-stone-500">analizzato</span>
              </h3>
              <p className="text-risco-muted mt-3 max-w-2xl">
                Fasi, permessi, categorie di costo e tempi indicativi. Nessun importo: la stima
                economica arriverà dopo il sopralluogo gratuito con i nostri tecnici.
              </p>

              <div
                className="risco-card p-6 md:p-8 mt-8 bg-white prose prose-stone max-w-none analysis-md"
                data-testid="sim-analysis-content"
              >
                <ReactMarkdown>{analysis.analysis}</ReactMarkdown>
              </div>

              <div
                className="mt-6 text-xs text-stone-500 leading-relaxed border-t border-risco-border/60 pt-4"
                data-testid="sim-disclaimer"
              >
                {analysis.disclaimer}
              </div>

              {submittedLead ? (
                <div
                  className="mt-8 risco-card p-6 md:p-7 border-risco-accent/50 bg-risco-accent-soft/60"
                  data-testid="sim-lead-success"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-risco-accent shrink-0 mt-0.5" />
                    <div>
                      <div className="font-heading text-lg text-risco-ink">
                        Analisi inviata a {contact.email}
                      </div>
                      <p className="text-sm text-risco-muted mt-2">
                        Il nostro team ti ricontatterà entro 48 ore lavorative per fissare
                        il sopralluogo gratuito e trasformare questa analisi in un preventivo concreto.
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* CTA sopralluogo */}
              <div
                className="mt-6 p-6 md:p-7 rounded-sm border border-risco-ink bg-risco-ink text-risco-bg"
                data-testid="sim-contact-cta"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                  <div className="flex-1">
                    <div className="text-xs uppercase tracking-[0.28em] text-risco-accent">
                      Prossimo passo
                    </div>
                    <h4 className="font-heading text-xl md:text-2xl mt-2 tracking-tight text-risco-bg">
                      Fissa un{" "}
                      <span className="font-display italic">sopralluogo gratuito</span>{" "}
                      con i nostri tecnici.
                    </h4>
                  </div>
                  <Link
                    to="/contatti"
                    data-testid="sim-cta-sopralluogo"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-risco-accent hover:bg-risco-accent-dark text-white font-semibold whitespace-nowrap transition-colors"
                  >
                    <PhoneCall className="w-4 h-4" />
                    Prenota sopralluogo
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Controls */}
      {step < 2 && (
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-risco-border">
          <button
            type="button"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="wizard-back"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md border border-stone-300 text-stone-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" /> Indietro
          </button>
          {step === 0 ? (
            <button
              type="button"
              disabled={!step1Valid}
              onClick={() => setStep(1)}
              data-testid="wizard-next"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-risco-accent hover:bg-risco-accent-dark disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold transition-colors"
            >
              Avanti <ArrowRight className="w-4 h-4" />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
